import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from '@/axios';
import { useAssessmentStore } from './assessmentStore';
import { setSessionCookie, removeSessionCookie } from '@/utils/sessionCookie';

export const useAuthStore = defineStore('auth', () => {

    const id = ref();
    const accessToken = ref(null);
    const clientAccessToken = ref(null);
    const subdomain = ref('app');
    const companyName = ref('')
    const language = ref(null);
    const role = ref(null);
    const email = ref(null);
    const name = ref(null);
    const lastName = ref(null);
    const phone = ref(null);
    const hasMF2 = ref(null);
    const baseUrl = ref(process.env.VUE_APP_BASE_URL);
    const firstAccess = ref(false);
    const isSuperAdmin = ref(false);
    const scope = ref({});
    const isTenant = ref(false);
    const permissions = ref([]);
    const scopeCount = ref(0);
    const scopesAssetCount = ref(0);
    const otpVerified = ref(false);
    const pendingUser = ref(null);
    const mspiRedirect = ref('');
    const isExistingUser = ref(false);
    const authenticated = ref(false);

    const clientId = ref(process.env.VUE_APP_CLIENT_ID);
    const clientSecret = ref(process.env.VUE_APP_CLIENT_SECRET);

    function setAuthData(userId, token, newSubdomain, newLanguage, newRole, newName, newLastName, newEmail, newPhone, newHasMF2, newIsSuperAdmin, newPermissions) {

        id.value = userId;
        accessToken.value = token;
        subdomain.value = newSubdomain;
        isTenant.value = newSubdomain !== 'app';
        language.value = newLanguage;
        name.value = newName;
        lastName.value = newLastName;
        email.value = newEmail;
        phone.value = newPhone;
        hasMF2.value = newHasMF2;
        role.value = newRole;
        isSuperAdmin.value = newIsSuperAdmin;
        permissions.value = newPermissions;

        if (isSuperAdmin.value) {

            permissions.value = newPermissions;

            scope.value = { id: 0 };

        }

        setSessionCookie(token, userId);
        authenticated.value = true;

    }

    function clearAuthData(removeCookie = true) {

        const assessmentStore = useAssessmentStore();

        id.value = null;
        accessToken.value = null;
        clientAccessToken.value = null;
        subdomain.value = '';
        language.value = null;
        role.value = null;
        name.value = null;
        lastName.value = null;
        email.value = null;
        scope.value = {};
        firstAccess.value = false;
        companyName.value = null;
        scopeCount.value = 0;
        scopesAssetCount.value = 0;
        otpVerified.value = false;
        pendingUser.value = null;
        authenticated.value = false;

        if (removeCookie) {

            removeSessionCookie();

        }

        assessmentStore.resetAssessment();

    }

    function setScope(newScope) {
        scope.value = newScope;
    }

    function setFirstAccess(access) {

        firstAccess.value = access === "true";

    }

    function setPendingUser(userData, userId, existingUser = false) {

        pendingUser.value = {
            id: userId,
            name: userData.name,
            lastName: userData.last_name,
            email: userData.email,
            phone: userData.phone,

        };

        isExistingUser.value = existingUser;

    }

    function setOtpVerified(access_token, userId) {

        otpVerified.value = true;
        accessToken.value = access_token;
        id.value = userId;

        setSessionCookie(access_token, userId);

        if (pendingUser.value) {

            const data = pendingUser.value;
            name.value = data.name;
            lastName.value = data.lastName || data.last_name;
            email.value = data.email;
            phone.value = data.phone;

            pendingUser.value = null;

        }

    }

    function setEmail(newEmail) {
        email.value = newEmail;
    }

    function setScopeCount(newScopeCount) {
        scopeCount.value = newScopeCount;
    }

    function setAssetScopeCount(newScopeAssetCount) {
        scopesAssetCount.value = newScopeAssetCount;
    }

    function setCompanyName(newCompanyName) {
        companyName.value = newCompanyName;
    }

    function clearEmail() {
        email.value = null;
    }

    function setTenantData(tenantData) {

        subdomain.value = tenantData.subdomain;
        companyName.value = tenantData.name;
        isTenant.value = tenantData.subdomain !== 'app';

    }

    function setSessionFromPayload(payload) {

        if (!payload || typeof payload !== 'object') return;

        id.value = payload.id ?? id.value;
        accessToken.value = payload.access_token ?? accessToken.value;
        hasMF2.value = payload.has_mf2 ?? hasMF2.value;
        name.value = payload.name ?? name.value;
        lastName.value = payload.lastName ?? payload.last_name ?? lastName.value;
        email.value = payload.email ?? email.value;
        phone.value = payload.phone ?? phone.value;

    }

    function setSessionFromMe(data) {

        if (!data || typeof data !== 'object') return;

        id.value = data.id ?? id.value;
        name.value = data.name ?? name.value;
        lastName.value = data.last_name ?? lastName.value;
        email.value = data.email ?? email.value;
        phone.value = data.phone ?? phone.value;
        hasMF2.value = data.has_mf2 ?? hasMF2.value;
        isSuperAdmin.value = data.is_super_admin ?? false;
        permissions.value = Array.isArray(data.permissions) ? data.permissions : permissions.value;

        if (data.role_id !== undefined && data.role_id !== null) {
            role.value = { id: data.role_id };
        }

        if (isSuperAdmin.value) {
            scope.value = { id: 0 };
        }

        authenticated.value = true;

    }

    function setMSPIRedirect(redirectData) {

        mspiRedirect.value = redirectData;

    }

    async function hydrateFromMe() {

        try {

            const { data } = await axios.get('auth/me', { skipAuthRedirect: true });

            if (data?.success && data?.data?.id) {

                setSessionFromMe(data.data);
                return true;

            }

        } catch (error) {

            console.error('auth/me hydrate:', error);

        }

        return false;
    }

    async function fetchClientToken() {

        try {

            const params = {

                grant_type: 'client_credentials',
                client_id: clientId.value,
                client_secret: clientSecret.value,
                scope: ''
            }

            const response = await axios.post('oauth/token', params, { skipAuthRedirect: true });

            clientAccessToken.value = response.data.access_token;
            return response.data;

        } catch (error) {

            console.error('Error obteniendo el token de cliente:', error);
            throw error;

        }
    }

    function hasPermission(permissionId) {
        return Array.isArray(permissions.value) && permissions.value.includes(permissionId);
    }


    return {
        id,
        accessToken,
        clientAccessToken,
        subdomain,
        companyName,
        language,
        role,
        baseUrl,
        email,
        firstAccess,
        name,
        phone,
        hasMF2,
        scope,
        scopeCount,
        scopesAssetCount,
        lastName,
        isExistingUser,
        clientId,
        clientSecret,
        otpVerified,
        pendingUser,
        setAuthData,
        clearAuthData,
        setEmail,
        setScope,
        setScopeCount,
        setAssetScopeCount,
        setCompanyName,
        isTenant,
        setFirstAccess,
        clearEmail,
        fetchClientToken,
        isSuperAdmin,
        permissions,
        hasPermission,
        setPendingUser,
        setOtpVerified,
        setTenantData,
        authenticated,
        setSessionFromPayload,
        setSessionFromMe,
        hydrateFromMe,
        setMSPIRedirect
    };

}, {
    persist: {

        storage: localStorage,
        omit: ['baseUrl', 'clientId', 'clientSecret'],

    },
});
